import {
  Button,
  OptionsMenu,
  SearchPage,
  type SearchPageTab,
} from '@farmshare/ui-components';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

import { Link } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { userState, vendorState } from 'state';

import {
  EnumUserStatus,
  Maybe,
  ProcessorCustomersPaginationQuery,
  ProcessorCustomersPaginationQueryVariables,
  SortFindManyUserInput,
  useProcessorCustomersPaginationLazyQuery,
  type User,
  type UserPagination,
  ViewProcessorAdminQueryResult,
} from '../../lib/graphql';

import {
  Box,
  Chip,
  Button as MuiButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { CheckCircle, Error, Warning } from '@mui/icons-material';
import { useAddCustomerModal } from './components/add-customer-dialog/use-add-customer-modal';
import { CustomerPreview } from './components/customer-preview';
import {
  formatCustomerDisplayName,
  formatPhoneDisplay,
  formatUserRelationshipType,
  isPhoneNumberValid,
  resolveCustomerDisplayName,
} from './helpers/formatting';
import { isAdmin } from 'lib/userHelpers';
import {
  type CustomerSummary,
  useDeleteCustomerModal,
  useMergeWizardModal,
} from 'features/merge-delete';

export interface ProcessorCustomersProps {
  settings?: Maybe<ViewProcessorAdminQueryResult>;
}

// Sentinel sort values for the computed latest-dropoff column. They aren't part
// of SortFindManyUserInput (it's a DB-field enum), so buildQuery translates them
// into the resolver's `latestDropoffSort` arg and passes a placeholder `$sort`.
const LATEST_DROPOFF_SORT = {
  asc: 'LATESTDROPOFF_ASC',
  desc: 'LATESTDROPOFF_DESC',
} as const;

type CustomerSort =
  | SortFindManyUserInput
  | (typeof LATEST_DROPOFF_SORT)[keyof typeof LATEST_DROPOFF_SORT];

// Preset views over the filters below. "All Customers" leads and stays the
// default, so the page opens on the same unfiltered roster it always has.
const CUSTOMER_TABS: SearchPageTab[] = [
  { value: 'all', label: 'All Customers' },
  {
    value: 'producers',
    label: 'Producers',
    filters: { relationshipType: { selectedValue: 'producer' } },
  },
  {
    value: 'buyers',
    label: 'Buyers',
    filters: { relationshipType: { selectedValue: 'buyer' } },
  },
  {
    value: 'rebooking',
    label: 'Rebooking Due',
    // Everyone worth a follow-up call — overdue first, then the ones about to
    // be. "On track" is deliberately left out; it needs no action.
    filters: { rebooking: { selectedValues: ['overdue', 'dueSoon'] } },
  },
  {
    value: 'incomplete',
    label: 'Incomplete',
    filters: { status: { selectedValue: EnumUserStatus.MissingData } },
  },
];

// Reusable styled button for accessible clickable text in tooltips
const TooltipEditButton = ({ onClick }: { onClick: () => void }) => (
  <MuiButton
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    sx={{
      color: 'inherit',
      textDecoration: 'underline',
      cursor: 'pointer',
      padding: 0,
      minWidth: 'auto',
      textTransform: 'none',
      fontWeight: 'inherit',
      fontSize: 'inherit',
      lineHeight: 'inherit',
      verticalAlign: 'baseline',
      '&:hover': {
        fontWeight: 'bold',
        textDecoration: 'underline',
        backgroundColor: 'transparent',
      },
    }}
    aria-label="Edit customer information"
  >
    Click here to edit
  </MuiButton>
);

const StatusIcon = ({
  user,
  onEditClick,
}: {
  user: User;
  onEditClick: (customer: User) => void;
}) => {
  const theme = useTheme();

  const { status } = user;
  const phoneIsInvalid =
    user.phone && !isPhoneNumberValid(user.phone, { allowEmpty: true });

  // Helper function to determine what's missing for partially_complete status
  const getMissingFields = () => {
    const missing: string[] = [];
    if (!user.phone) missing.push('phone');
    if (!user.email) missing.push('email');
    return missing;
  };

  const getTooltipContent = () => {
    const userName =
      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      'This customer';

    // If phone is invalid, always show as partially complete
    if (phoneIsInvalid) {
      const missingFields = getMissingFields();
      const hasMissingFields = missingFields.length > 0;

      return (
        <Box>
          {hasMissingFields ? (
            <>
              {userName} is missing {missingFields.join(' or ')}.{' '}
            </>
          ) : (
            ''
          )}
          The phone number may be invalid.{' '}
          <TooltipEditButton onClick={() => onEditClick(user)} />
        </Box>
      );
    }

    switch (status) {
      case EnumUserStatus.Complete:
        return 'All customer data has been provided.';
      case EnumUserStatus.PartiallyComplete: {
        const missingFields = getMissingFields();
        const missingText = missingFields.join(' or ');
        return (
          <Box>
            {userName} is missing {missingText}.{' '}
            <TooltipEditButton onClick={() => onEditClick(user)} />
          </Box>
        );
      }
      case EnumUserStatus.MissingData:
        return (
          <Box>
            This customer record is missing data.{' '}
            <TooltipEditButton onClick={() => onEditClick(user)} />
          </Box>
        );
      default:
        return '';
    }
  };

  // Determine which icon to show (phone invalid always shows warning)
  // The UI may show incomplete status if the number is invalid while the db may show complete.
  // This is intentionally incomplete to allow for future enhancements.
  const effectiveStatus = phoneIsInvalid
    ? EnumUserStatus.PartiallyComplete
    : status;

  switch (effectiveStatus) {
    case EnumUserStatus.Complete:
      return (
        <Tooltip title={getTooltipContent()} arrow>
          <CheckCircle
            sx={{ color: theme.palette.primary.main, fontSize: '1.25rem' }}
            aria-label="Customer information complete"
          />
        </Tooltip>
      );
    case EnumUserStatus.PartiallyComplete:
      return (
        <Tooltip title={getTooltipContent()} arrow>
          <Warning
            sx={{ color: theme.palette.warning.main, fontSize: '1.25rem' }}
            aria-label="Customer information partially complete"
          />
        </Tooltip>
      );
    case EnumUserStatus.MissingData:
      return (
        <Tooltip title={getTooltipContent()} arrow>
          <Error
            sx={{ color: theme.palette.error.main, fontSize: '1.25rem' }}
            aria-label="Customer information missing data"
          />
        </Tooltip>
      );
    default:
      return null;
  }
};

// Rebooking-cadence buckets returned by the resolver (`rebookingStatus`), shown
// next to the latest dropoff date. Omitted entirely when null (fewer than two
// bookings → no cadence estimate).
const REBOOKING_CHIP: Record<
  string,
  { label: string; color: 'error' | 'warning' | 'success' | 'default' }
> = {
  overdue: { label: 'Overdue', color: 'error' },
  dueSoon: { label: 'Due soon', color: 'warning' },
  onTrack: { label: 'On track', color: 'success' },
  // Customer with a single booking — not enough history for a cadence estimate.
  new: { label: 'New', color: 'default' },
};

// Where the interval behind the chip came from — surfaced as a tooltip, with a
// manual override outlined so it visibly differs from history/default.
const REBOOKING_SOURCE_TOOLTIP: Record<string, string> = {
  manual: 'Rebooking interval set manually',
  auto: 'Rebooking interval from booking history',
  default: 'Rebooking interval from processor default',
};

const RebookingChip = ({
  status = undefined,
  source = undefined,
}: {
  status?: Maybe<string>;
  source?: Maybe<string>;
}) => {
  const config = status ? REBOOKING_CHIP[status] : undefined;
  if (!config) return null;
  const chip = (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      variant={source === 'manual' ? 'outlined' : 'filled'}
    />
  );
  const tooltip = source ? REBOOKING_SOURCE_TOOLTIP[source] : undefined;
  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
};

/**
 * Latest-dropoff cell: the rebooking chip above the formatted dropoff date.
 * Module-scope so the table doesn't get a brand-new cell renderer each render.
 * @param row
 */
const formatLatestDropoffCell = (row: User) =>
  row?.latestDropoffDate ? (
    <Stack spacing={0.5} alignItems="flex-start">
      <RebookingChip
        status={row.rebookingStatus}
        source={row.rebookingIntervalSource}
      />
      <Typography variant="caption" color="text.secondary">
        {moment.utc(row.latestDropoffDate).format('LL')}
      </Typography>
    </Stack>
  ) : (
    '-'
  );

/**
 * Builds the status-icon cell renderer, bound to the page's edit handler.
 * Module-scope for the same reason as `formatLatestDropoffCell`.
 * @param onEditClick
 */
const formatStatusCell =
  (onEditClick: (customer: User) => void) => (row: User) => (
    <StatusIcon user={row} onEditClick={onEditClick} />
  );

export function ProcessorCustomers(_props: ProcessorCustomersProps) {
  const vendor = useRecoilValue(vendorState);
  const user = useRecoilValue(userState);
  // Phase 1 visibility gate: merge tooling is admin-only until the Phase 2
  // flip adds vendor / vendor-owner roles.
  const canMerge = isAdmin(user);

  const [_loadData, queryResult] = useProcessorCustomersPaginationLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const processorCustomersPaginationQuery: [
    typeof _loadData,
    typeof queryResult,
  ] = [_loadData, queryResult];

  const openAddCustomer = useAddCustomerModal();
  const openMergeWizard = useMergeWizardModal();
  const openDeleteCustomer = useDeleteCustomerModal();

  const handleCustomerSuccess = async () => {
    // Refetch the customer list to show updated data
    if (queryResult.refetch) {
      await queryResult.refetch();
    }
  };

  const handleEditCustomer = (customer: User) => {
    const userName =
      `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
    openAddCustomer({
      customer,
      title: userName ? `Edit ${userName}` : 'Edit Customer',
      onSuccess: handleCustomerSuccess,
    });
  };

  const toCustomerSummary = (row: User): CustomerSummary => ({
    _id: row._id,
    first_name: row.first_name,
    last_name: row.last_name,
    email: row.email,
    phone: row.phone,
    company: row.company,
  });

  // Merge flow (admin-only). The kebab "Merge…" item opens the 3-step wizard
  // with this customer as the initiating record.
  const handleMergeCustomer = (initiatingCustomer: CustomerSummary) =>
    openMergeWizard({
      vendorId: vendor?._id,
      initiatingCustomer,
      onCompleted: handleCustomerSuccess,
    });

  // Delete flow (admin-only). Pre-flights associated records; zero → confirm
  // delete, otherwise → route into the merge wizard.
  const handleDeleteCustomer = (row: User) =>
    openDeleteCustomer({
      vendorId: vendor?._id,
      customer: toCustomerSummary(row),
      onDeleted: handleCustomerSuccess,
      onMergeInstead: handleMergeCustomer,
    });

  const customerLink = (row: User) => (
    <Link to={`/processor/customers/${row._id}`}>
      {formatCustomerDisplayName(row)}
    </Link>
  );

  const customerOptions = (row: User) => (
    <Box sx={{ width: 1, display: 'flex', justifyContent: 'flex-end' }}>
      <OptionsMenu
        items={[
          {
            label: 'View',
            href: `/processor/customers/${row._id}`,
          },
          {
            label: 'Edit',
            onClick: () => handleEditCustomer(row),
          },
          ...(canMerge
            ? [
                {
                  label: 'Merge…',
                  onClick: () => handleMergeCustomer(toCustomerSummary(row)),
                },
                {
                  label: 'Delete',
                  onClick: () => handleDeleteCustomer(row),
                },
              ]
            : []),
        ]}
        ariaLabel="Customer Options"
      />
    </Box>
  );

  return (
    <SearchPage<
      User,
      UserPagination,
      ProcessorCustomersPaginationQuery,
      ProcessorCustomersPaginationQueryVariables,
      CustomerSort
    >
      tabs={CUSTOMER_TABS}
      defaultTab="all"
      header={{
        title: 'Customers',
        subtitle: 'Manage your customers',
        actions: (
          <Button
            content="Add Customer"
            icon={faPlus}
            onClick={(e) => {
              e.preventDefault();
              openAddCustomer({ onSuccess: handleCustomerSuccess });
            }}
          />
        ),
      }}
      paginationQuery={processorCustomersPaginationQuery}
      dataAccessor={(data) => data.getProcessorCustomers as UserPagination}
      columns={[
        {
          label: '',
          formatter: formatStatusCell(handleEditCustomer),
        },
        {
          label: 'Name',
          field: 'first_name',
          formatter: (row: User) => customerLink(row),
        },
        {
          label: 'Type',
          formatter: (row: User) =>
            formatUserRelationshipType(row, vendor?._id),
        },
        {
          label: 'Producer',
          field: 'company',
          formatter: (row: User) => row.company || '-',
        },
        {
          label: 'Email',
          field: 'email',
          minimumBreakpoint: 'md',
          formatter: (row: User) => row?.email || '-',
        },
        {
          label: 'Phone',
          field: 'phone',
          minimumBreakpoint: 'md',
          formatter: (row: User) => formatPhoneDisplay(row?.phone),
        },
        {
          label: 'Latest Dropoff',
          field: 'latestDropoffDate',
          minimumBreakpoint: 'md',
          formatter: formatLatestDropoffCell,
        },
        {
          label: '',
          formatter: (row: User) => customerOptions(row),
        },
      ]}
      sortOptions={[
        {
          field: 'name',
          label: 'Name',
          direction: 'asc',
          value: SortFindManyUserInput.NameAsc,
        },
        {
          field: 'name',
          label: 'Name',
          direction: 'desc',
          value: SortFindManyUserInput.NameDesc,
        },
        {
          field: 'latestDropoff',
          label: 'Latest Dropoff',
          direction: 'asc',
          value: LATEST_DROPOFF_SORT.asc,
        },
        {
          field: 'latestDropoff',
          label: 'Latest Dropoff',
          direction: 'desc',
          value: LATEST_DROPOFF_SORT.desc,
        },
      ]}
      defaultSort={SortFindManyUserInput.NameAsc}
      buildQuery={(searchValue, selectedFilters, sort, page, perPage) => {
        const filterObj: Record<string, string> = {};
        let rebookingStatuses: string[] | undefined;

        // Process each active filter
        selectedFilters.forEach((filter) => {
          if (filter.isActive) {
            if (filter.value === 'relationshipType' && filter.selectedValue) {
              filterObj.relationshipType = filter.selectedValue;
            }
            if (filter.value === 'status' && filter.selectedValue) {
              filterObj.status = filter.selectedValue;
            }
            if (filter.value === 'rebooking' && filter.selectedValues?.length) {
              rebookingStatuses = filter.selectedValues;
            }
            // Single-option date-field-range (mirrors the jobs search page);
            // the only field is Latest Dropoff, so map its range straight to
            // the resolver's latestDropoffDate bounds.
            if (filter.value === 'date') {
              if (filter.selectedDateFrom) {
                filterObj.latestDropoffDateFrom = filter.selectedDateFrom;
              }
              if (filter.selectedDateTo) {
                filterObj.latestDropoffDateTo = filter.selectedDateTo;
              }
            }
            // Add more filter handling as needed
          }
        });

        const sortByLatestDropoff =
          sort === LATEST_DROPOFF_SORT.asc || sort === LATEST_DROPOFF_SORT.desc;

        let latestDropoffSort: 'ASC' | 'DESC' | undefined;
        if (sort === LATEST_DROPOFF_SORT.asc) {
          latestDropoffSort = 'ASC';
        } else if (sort === LATEST_DROPOFF_SORT.desc) {
          latestDropoffSort = 'DESC';
        }

        return {
          ...filterObj,
          rebookingStatuses,
          vendorId: vendor?._id,
          search: searchValue,
          // The computed latest-dropoff sort rides on its own arg; `$sort` is
          // required, so pass a harmless placeholder the resolver ignores.
          sort: sortByLatestDropoff
            ? SortFindManyUserInput.NameAsc
            : (sort as SortFindManyUserInput),
          latestDropoffSort,
          page,
          perPage,
        };
      }}
      renderPreview={(customer) => <CustomerPreview customer={customer} />}
      previewConfig={(customer) => {
        const { name, field: nameField } = resolveCustomerDisplayName(customer);
        const type = formatUserRelationshipType(customer, vendor?._id);
        return {
          status: type,
          title: name,
          subtitle:
            nameField === 'company' ? undefined : customer.company || undefined,
          href: `/processor/customers/${customer._id}`,
          action: (
            <Button
              content="Edit Customer"
              variant="outlined"
              size="sm"
              onClick={() => handleEditCustomer(customer)}
            />
          ),
        };
      }}
      searchPlaceholder="Search customer by name, email, phone, type or farm"
      filters={[
        {
          label: 'Type',
          value: 'relationshipType',
          type: 'select',
          options: [
            { label: 'Producer', value: 'producer' },
            { label: 'Buyer', value: 'buyer' },
          ],
        },
        {
          label: 'Status',
          value: 'status',
          type: 'select',
          options: [
            { label: 'Complete', value: EnumUserStatus.Complete },
            {
              label: 'Partially Complete',
              value: EnumUserStatus.PartiallyComplete,
            },
            {
              label: 'Incomplete',
              value: EnumUserStatus.MissingData,
            },
          ],
        },
        {
          label: 'Date',
          value: 'date',
          type: 'date-field-range' as const,
          options: [{ label: 'Latest Dropoff', value: 'latestDropoff' }],
        },
        {
          label: 'Rebooking',
          value: 'rebooking',
          type: 'multi-select' as const,
          options: [
            { label: 'Overdue', value: 'overdue' },
            { label: 'Due soon', value: 'dueSoon' },
            { label: 'On track', value: 'onTrack' },
          ],
        },
      ]}
    />
  );
}
